import { useCallback } from 'react'
import { useSettingsRouteState } from './useSettingsRouteState'
import { useUserPreferences } from './useUserPreferences'
import type { UserPreferences } from '@/utils/userPreferences'

// Map component entity to route entity and preferences key
const ENTITY_TO_ROUTE_MAP: Record<string, string> = {
	'billing-profiles': 'billing-profiles',
	'users': 'users',
	'locations': 'locations',
	'domains': 'domains',
	'phone-numbers': 'phone-numbers',
	'data-sources': 'data-sources',
	'connections': 'connections',
} as const

const ENTITY_TO_PREFERENCE_KEY: Record<string, keyof UserPreferences['viewPreferences']> = {
	'billing-profiles': 'billingProfiles',
	'users': 'users',
	'locations': 'locations',
	'domains': 'domains',
	'phone-numbers': 'phoneNumbers',
	'data-sources': 'dataSources',
	'connections': 'connections',
} as const

export interface SettingsPanelState {
	isOpen: boolean
	recordId: string | null
	view: 'table' | 'cards'
	mode: 'view' | 'new'
}

export function useSettingsPanelState(entity: string) {
	const routeState = useSettingsRouteState()
	const { getView, updateViewPreference } = useUserPreferences()

	const routeEntity = ENTITY_TO_ROUTE_MAP[entity] || entity
	const preferenceKey = ENTITY_TO_PREFERENCE_KEY[entity]

	// Get view from user preferences, fallback to 'table'
	const view = preferenceKey ? getView(preferenceKey) : 'table'

	// Derive state from route params
	const state: SettingsPanelState = {
		isOpen: routeState.isPanelOpen && routeState.entity === routeEntity,
		recordId: routeState.recordId || null,
		view,
		mode: routeState.isNew ? 'new' : 'view',
	}

	const openCreate = useCallback(() => {
		routeState.openCreate(routeEntity)
	}, [routeState, routeEntity])

	const openRecord = useCallback((recordId: string) => {
		routeState.openRecord(routeEntity, recordId)
	}, [routeState, routeEntity])

	const closePanel = useCallback(() => {
		routeState.closePanel(routeEntity)
	}, [routeState, routeEntity])

	const setView = useCallback((newView: 'table' | 'cards') => {
		if (preferenceKey) {
			updateViewPreference(preferenceKey, newView)
		}
	}, [preferenceKey, updateViewPreference])

	return {
		state,
		openCreate,
		openRecord,
		closePanel,
		setView,
	}
}